/**
 * NotificationBell — Navbar notification icon with unread badge and dropdown.
 * Only rendered when user is authenticated (parent gates this).
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  EmojiEvents,
  School,
  CardGiftcard,
  Campaign,
  Settings,
  Circle,
  Close as CloseIcon,
} from '@mui/icons-material';
import { riskAssessmentPath } from '../../utils/riskAssessmentRoutes';
import { useAuth } from '../../contexts/AuthContext';
import { useNotificationStore } from '../../stores/notificationStore';
import { typography } from '../../common/styles/universalTheme';
import type { UserNotification } from '../../types/user';

const ICON_BY_TYPE: Record<UserNotification['type'], React.ReactElement> = {
  level_up: <EmojiEvents sx={{ fontSize: 18, color: 'text.primary' }} />,
  quiz_unlocked: <School sx={{ fontSize: 18, color: 'text.primary' }} />,
  challenge_unlocked: <EmojiEvents sx={{ fontSize: 18, color: 'text.primary' }} />,
  discount_earned: <CardGiftcard sx={{ fontSize: 18, color: 'text.primary' }} />,
  news: <Campaign sx={{ fontSize: 18, color: 'text.secondary' }} />,
  system: <Settings sx={{ fontSize: 18, color: 'text.secondary' }} />,
  risk_alert: <NotificationsActive sx={{ fontSize: 18, color: 'error.main' }} />,
  rebalance_signal: <NotificationsActive sx={{ fontSize: 18, color: 'text.primary' }} />,
};

function formatTimeAgo(date: Date | string): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(date).toLocaleDateString();
}

export default function NotificationBell() {
  const { user } = useAuth();
  const { notifications, unreadCount, markRead, markAllRead, deleteNotification } = useNotificationStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleMarkAllRead = useCallback(() => {
    if (user?.uid) {
      markAllRead(user.uid);
    }
  }, [user?.uid, markAllRead]);

  const handleClickNotification = useCallback(
    (notif: UserNotification) => {
      if (!notif.read && user?.uid) {
        markRead(user.uid, notif.id);
      }
      // Take the reader to the portfolio the notification is about.
      //
      // The backend attaches `portfolioId` to `risk_alert` and
      // `rebalance_signal` precisely so this can happen, and
      // `PortfolioAnalysis` already auto-loads from `?load=`. Without the
      // navigation the $200-tier drift nudge said "your allocation has drifted
      // enough to be worth a look" and then left the reader to find the
      // portfolio themselves — with the simulation that answers it one click
      // away (BUGS #104). Every piece existed; nothing joined them.
      //
      // Notifications with no portfolioId (news, system, level-up) are
      // unaffected: marking read stays the whole behaviour.
      if (notif.portfolioId) {
        setAnchorEl(null);
        // A drift nudge exists to be answered by the simulation (A.20.3): carry
        // that intent through the load-and-run flow, so the reader lands on the
        // rebalance block the moment their result renders instead of hunting
        // below the fold for the section the notification was about.
        navigate(notif.type === 'rebalance_signal'
          ? riskAssessmentPath.rebalance(notif.portfolioId)
          : riskAssessmentPath.run(notif.portfolioId));
      }
    },
    [user?.uid, markRead, navigate],
  );

  const handleDeleteNotification = useCallback(
    (e: React.MouseEvent, notifId: string) => {
      e.stopPropagation();
      if (user?.uid) {
        deleteNotification(user.uid, notifId);
      }
    },
    [user?.uid, deleteNotification],
  );

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          sx={{
            mr: 1,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: typography.fontSize.xs,
                minWidth: 16,
                height: 16,
              },
            }}
          >
            <Notifications />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxHeight: 400,
              overflow: 'auto',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={typography.fontWeight.semibold}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead} sx={{ fontSize: typography.fontSize.xs, textTransform: 'none' }}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {/* Empty state */}
        {notifications.length === 0 && (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        )}

        {/* Notification items */}
        {notifications.slice(0, 20).map((notif) => (
          <MenuItem
            key={notif.id}
            onClick={() => handleClickNotification(notif)}
            sx={{
              py: 1.5,
              px: 2,
              alignItems: 'flex-start',
              bgcolor: notif.read ? 'transparent' : 'action.hover',
              whiteSpace: 'normal',
              '&:hover .notif-delete-btn': { opacity: 1 },
            }}
          >
            <Box sx={{ mr: 1.5, mt: 0.25 }}>
              {ICON_BY_TYPE[notif.type] ?? ICON_BY_TYPE.system}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {!notif.read && (
                  <Circle sx={{ fontSize: 8, color: 'primary.main', flexShrink: 0 }} />
                )}
                <Typography
                  variant="body2"
                  fontWeight={notif.read ? typography.fontWeight.regular : typography.fontWeight.semibold}
                  noWrap
                  sx={{ flex: 1 }}
                >
                  {notif.title}
                </Typography>
                <IconButton
                  className="notif-delete-btn"
                  size="small"
                  onClick={(e) => handleDeleteNotification(e, notif.id)}
                  sx={{
                    p: 0.25,
                    opacity: 0,
                    transition: 'opacity 0.15s',
                    color: 'text.secondary',
                    flexShrink: 0,
                    '&:hover': { color: 'error.main' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}
              >
                {notif.message}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                {formatTimeAgo(notif.createdAt)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
